import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from "lucide-react";
import { useLogout } from "@/hooks/useLogout";
import type { SidebarMenuItem } from "@/lib/rbac/sidebar-config";
import {
  findNavMatch,
  useVisibleNav,
  type VisibleNavGroup as VisibleGroup,
  type VisibleNavSection as VisibleSection,
} from "@/lib/rbac/use-nav";
import { cn } from "@/lib/utils";

import UserItem from "./UserItem";
import { Button } from "./button";
import { ConfirmDialog } from "./confirm-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const COLLAPSED_STORAGE_KEY = "sidebar_collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean) {
  try {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // Storage unavailable — preference just won't persist
  }
}

const includes = (text: string | undefined, q: string) =>
  !!text && text.toLowerCase().includes(q);

export default function Sidebar({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const sections = useVisibleNav();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logoutMutation = useLogout();

  // ─── Collapse (desktop only; the mobile drawer is always full width) ──────
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const compact = collapsed && !isOpen;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      writeCollapsed(!prev);
      return !prev;
    });
  };

  // ─── Search ────────────────────────────────────────────────────────────────
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const q = query.trim().toLowerCase();

  const focusSearch = useCallback(() => {
    setCollapsed(false);
    writeCollapsed(false);
    // Wait for the expanded layout to render the input
    requestAnimationFrame(() => searchRef.current?.focus());
  }, []);

  // "/" focuses the menu search from anywhere outside a text field
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      e.preventDefault();
      focusSearch();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [focusSearch]);

  // Apply the search query: a section / group hit keeps all its items,
  // otherwise keep only items whose label or subgroup matches.
  const visibleSections = useMemo<VisibleSection[]>(() => {
    if (!q) return sections;
    return sections
      .map((section) => {
        const sectionHit = includes(section.label, q);
        return {
          ...section,
          groups: section.groups
            .map((group) =>
              sectionHit || includes(group.label, q)
                ? group
                : {
                    ...group,
                    items: group.items.filter(
                      (item) => includes(item.label, q) || includes(item.subgroup, q)
                    ),
                  }
            )
            .filter((group) => group.items.length > 0),
        };
      })
      .filter((section) => section.groups.length > 0);
  }, [sections, q]);

  const firstMatch = visibleSections[0]?.groups[0]?.items[0];

  const activeMatch = useMemo(() => findNavMatch(pathname, sections), [pathname, sections]);
  const activeItemId = activeMatch?.item.id;
  const activeGroupId = activeMatch?.group.id ?? null;

  const isItemActive = useCallback(
    (item: SidebarMenuItem) => item.id === activeItemId,
    [activeItemId]
  );

  // Track which group is expanded (one at a time)
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  // Auto-expand the group containing the active route
  useEffect(() => {
    if (activeGroupId) setOpenGroupId(activeGroupId);
  }, [activeGroupId]);

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => navigate("/login", { replace: true }),
      onError: () => navigate("/login", { replace: true }),
    });
  };

  const toggleGroup = (groupId: string) => {
    setOpenGroupId((prev) => (prev === groupId ? null : groupId));
  };

  const handleNavigate = () => {
    setQuery("");
    onClose();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      if (query) {
        setQuery("");
      } else {
        searchRef.current?.blur();
      }
    } else if (e.key === "Enter" && firstMatch) {
      navigate(firstMatch.path);
      handleNavigate();
      searchRef.current?.blur();
    }
  };

  return (
    <div
      className={cn(
        "fixed top-0 left-0 h-full w-[272px] bg-sidebar border-r border-sidebar-border z-50",
        "transition-[transform,width] duration-300",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "md:translate-x-0 md:sticky md:top-0 md:h-screen md:shrink-0",
        "flex flex-col",
        compact && "md:w-[68px]"
      )}
    >
      {/* Logo */}
      <div className={cn("pt-5 pb-2", compact ? "px-0 flex justify-center" : "px-5")}>
        <Link to="/dashboard" className="flex items-center gap-2.5" title="TudoNum">
          <img
            src="/navlogo.png"
            alt="Tudonum"
            className="h-8 w-8 rounded-lg object-contain"
          />
          {!compact && (
            <div className="flex flex-col leading-tight">
              <span className="text-[15px] font-bold tracking-tight text-sidebar-foreground">
                Tudo<span className="text-sidebar-primary">Num</span>
              </span>
              <span className="text-[10px] font-medium text-muted-foreground tracking-wide uppercase">
                Command Center
              </span>
            </div>
          )}
        </Link>
      </div>

      {/* User Info */}
      <div className="px-3 py-2">
        <UserItem compact={compact} />
      </div>

      {/* Search */}
      <div className="px-3 pb-1">
        {compact ? (
          <button
            type="button"
            onClick={focusSearch}
            aria-label="Search menu"
            title="Search menu ( / )"
            className="flex h-9 w-full items-center justify-center rounded-lg text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors"
          >
            <Search className="h-4 w-4" />
          </button>
        ) : (
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search menu…"
              aria-label="Search menu"
              className="h-9 w-full rounded-lg border border-sidebar-border bg-background pl-8 pr-8 text-[13px] text-sidebar-foreground placeholder:text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  searchRef.current?.focus();
                }}
                aria-label="Clear search"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-sidebar-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-sidebar-border px-1.5 text-[10px] font-medium text-muted-foreground">
                /
              </kbd>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav
        aria-label="Main navigation"
        className="flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll px-3 pt-1 pb-3"
      >
        {visibleSections.length === 0 && (
          <p className="px-2.5 py-6 text-center text-[13px] text-muted-foreground">
            No pages match “{query.trim()}”
          </p>
        )}

        {visibleSections.map((section) => (
          <div key={section.id} className="mt-3 first:mt-0">
            {section.label &&
              (compact ? (
                <div className="mx-2 mb-2 border-t border-sidebar-border" />
              ) : (
                <h3 className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                  {section.label}
                </h3>
              ))}
            <div className="space-y-0.5">
              {section.groups.map((group) => {
                if (group.isSingle) {
                  return (
                    <NavLink
                      key={group.id}
                      to={group.items[0].path}
                      icon={group.icon}
                      label={group.label}
                      active={isItemActive(group.items[0])}
                      compact={compact}
                      onNavigate={handleNavigate}
                    />
                  );
                }
                if (compact) {
                  return (
                    <GroupFlyout
                      key={group.id}
                      group={group}
                      containsActive={activeGroupId === group.id}
                      isItemActive={isItemActive}
                      onNavigate={handleNavigate}
                    />
                  );
                }
                return (
                  <SidebarGroup
                    key={group.id}
                    group={group}
                    // While searching, show every matching group expanded
                    isOpen={!!q || openGroupId === group.id}
                    containsActive={activeGroupId === group.id}
                    onToggle={() => toggleGroup(group.id)}
                    isItemActive={isItemActive}
                    onNavigate={handleNavigate}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer: collapse + logout */}
      <div className="p-3 border-t border-sidebar-border space-y-0.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleCollapsed}
          aria-label={compact ? "Expand sidebar" : "Collapse sidebar"}
          title={compact ? "Expand sidebar" : "Collapse sidebar"}
          className={cn(
            "hidden md:flex items-center gap-2 w-full text-muted-foreground hover:text-sidebar-foreground",
            compact ? "justify-center px-0" : "justify-start"
          )}
        >
          {compact ? (
            <PanelLeftOpen className="w-4 h-4" />
          ) : (
            <>
              <PanelLeftClose className="w-4 h-4" /> Collapse
            </>
          )}
        </Button>
        <ConfirmDialog
          onConfirm={handleLogout}
          title="Log out?"
          description="You will be redirected to login and your session will end."
          confirmLabel="Log Out"
        >
          <Button
            variant="ghost"
            size="sm"
            aria-label="Log out"
            title={compact ? "Log out" : undefined}
            className={cn(
              "flex items-center gap-2 w-full text-destructive hover:text-destructive hover:bg-destructive/10",
              compact ? "justify-center px-0" : "justify-start"
            )}
          >
            <LogOut className="w-4 h-4" />
            {!compact && "Log Out"}
          </Button>
        </ConfirmDialog>
      </div>
    </div>
  );
}

/* ─── Top-level link (single-item groups) ─────────────────────────────────── */

function NavLink({
  to,
  icon: Icon,
  label,
  active,
  compact,
  onNavigate,
}: {
  to: string;
  icon: SidebarMenuItem["icon"];
  label: string;
  active: boolean;
  compact: boolean;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      className={cn(
        "flex items-center gap-2.5 py-2 text-[13px] font-medium rounded-lg transition-colors",
        compact ? "justify-center px-0" : "px-2.5",
        active
          ? "bg-sidebar-primary text-sidebar-primary-foreground"
          : "text-sidebar-foreground hover:bg-sidebar-accent"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!compact && <span className="truncate">{label}</span>}
    </Link>
  );
}

/* ─── Item list (shared by accordion + flyout) ────────────────────────────── */

function GroupItems({
  items,
  isItemActive,
  onNavigate,
}: {
  items: SidebarMenuItem[];
  isItemActive: (item: SidebarMenuItem) => boolean;
  onNavigate: () => void;
}) {
  return (
    <>
      {items.map((item, i) => {
        const active = isItemActive(item);
        const showSubgroup = item.subgroup && item.subgroup !== items[i - 1]?.subgroup;
        return (
          <li key={item.id}>
            {showSubgroup && (
              <div
                className={cn(
                  "px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70",
                  i === 0 ? "pt-1.5" : "pt-3"
                )}
              >
                {item.subgroup}
              </div>
            )}
            <Link
              to={item.path}
              onClick={onNavigate}
              aria-current={active ? "page" : undefined}
              className={cn(
                "my-0.5 flex items-center gap-2.5 px-2 py-1.5 text-[13px] rounded-md transition-colors",
                active
                  ? "bg-sidebar-primary text-sidebar-primary-foreground font-medium"
                  : "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent"
              )}
            >
              <item.icon className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          </li>
        );
      })}
    </>
  );
}

/* ─── Group Accordion (expanded sidebar) ──────────────────────────────────── */

function SidebarGroup({
  group,
  isOpen,
  containsActive,
  onToggle,
  isItemActive,
  onNavigate,
}: {
  group: VisibleGroup;
  isOpen: boolean;
  containsActive: boolean;
  onToggle: () => void;
  isItemActive: (item: SidebarMenuItem) => boolean;
  onNavigate: () => void;
}) {
  const Icon = group.icon;
  const panelId = `sidebar-group-${group.id}`;

  return (
    <div>
      {/* Group Header */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={cn(
          "flex items-center gap-2.5 w-full px-2.5 py-2 text-[13px] font-medium rounded-lg transition-colors cursor-pointer",
          "text-sidebar-foreground hover:bg-sidebar-accent",
          containsActive && !isOpen && "bg-sidebar-accent font-semibold"
        )}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate text-left">{group.label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200",
            !isOpen && "-rotate-90"
          )}
        />
      </button>

      {/* Group Items */}
      <div
        id={panelId}
        className={cn(
          "grid transition-[grid-template-rows] duration-200",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <ul
          className="overflow-hidden ml-[18px] pl-2 border-l border-sidebar-border"
          // Keep collapsed links out of the tab order
          inert={!isOpen}
        >
          <GroupItems items={group.items} isItemActive={isItemActive} onNavigate={onNavigate} />
        </ul>
      </div>
    </div>
  );
}

/* ─── Group Flyout (collapsed sidebar) ────────────────────────────────────── */

function GroupFlyout({
  group,
  containsActive,
  isItemActive,
  onNavigate,
}: {
  group: VisibleGroup;
  containsActive: boolean;
  isItemActive: (item: SidebarMenuItem) => boolean;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = group.icon;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={group.label}
          title={group.label}
          className={cn(
            "flex w-full items-center justify-center py-2 rounded-lg transition-colors cursor-pointer",
            containsActive
              ? "bg-sidebar-primary text-sidebar-primary-foreground"
              : "text-sidebar-foreground hover:bg-sidebar-accent",
            open && !containsActive && "bg-sidebar-accent"
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" sideOffset={10} className="w-60 p-1.5">
        <div className="px-2 pt-1 pb-1.5 text-[13px] font-semibold text-foreground">
          {group.label}
        </div>
        <ul className="max-h-[70vh] overflow-y-auto sidebar-scroll">
          <GroupItems
            items={group.items}
            isItemActive={isItemActive}
            onNavigate={() => {
              setOpen(false);
              onNavigate();
            }}
          />
        </ul>
      </PopoverContent>
    </Popover>
  );
}
